import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { Audience, ViewCategory, VisibilityType } from '@ddt-web/config';
import { ClientOutput } from '@ddt-web/domain';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as set from 'lodash/set';
import { combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';

import { CellData } from '../models/cell-data';
import { ComponentData } from '../models/component-data';
import { ErrorData } from '../models/error-data';
import { CellRow } from '../models/interfaces';
import { TabData } from '../models/tab-data';
import { TabDisplayConfig } from '../models/tab-display-config';
import { TableData } from '../models/table-data';
import { ComponentVisibilityMapping } from '../models/visibility-config';
import { TabUtil, VisibilityConfigUtils } from '../utils';
import { AudienceService } from './audience.service';
import { ViewDataFactoryService } from './view-data-factory.service';

@Injectable()
export class DisplayDataFactoryService {
	private readonly PDF_VIEW_CATEGORIES: ViewCategory[] = [ViewCategory.FA_PLUS_PDF, ViewCategory.FA_SELF_SERVICE_PDF, ViewCategory.CLIENT_PDF, ViewCategory.PROPOSAL_PDF];

	private componentVisibilityConfig: ComponentVisibilityMapping;
	private readonly _defaultIds$: Subject<string[]> = new ReplaySubject(1);

	public isPartnerUser: boolean = false;

	constructor(
		private viewDataFactory: ViewDataFactoryService,
		private audienceService: AudienceService
	) { }

	public initialise(viewCategory: ViewCategory, componentVisibilityConfig: ComponentVisibilityMapping, visibility?: VisibilityType): void {
		// initialise and get audience
		visibility ? this.audienceService.initialiseAudienceWithVisibility(visibility, viewCategory) : this.audienceService.initialiseAudience(viewCategory);
		this.setComponentVisibilityConfig(componentVisibilityConfig);
	}

	public setDefaultIds(ids: string[]): void {
		this._defaultIds$.next(ids);
	}

	public setComponentVisibilityConfig(componentVisibilityConfig: ComponentVisibilityMapping): void {
		this.componentVisibilityConfig = componentVisibilityConfig;
	}

	public checkComponentVisibility(componentId: string): Observable<boolean> {
		return combineLatest([this.audienceService.getViewCategory(), this.audienceService.getAudience()]).pipe(
			map(([viewCategory, audience]) => {
				return this.checkComponentVisibilityForViewCategory(componentId, viewCategory, audience);
			})
		);
	}

	private checkComponentVisibilityForViewCategory(componentId: string, viewCategory: ViewCategory, audience: Audience, isPublicView?: VisibilityType): boolean {
		const componentConfig = this.componentVisibilityConfig[componentId];

		if (!componentConfig) {
			console.error(`There is no configuration for ${componentId} in viewCategory ${viewCategory} - audience ${JSON.stringify(audience)}`);
			return false;
		}
		return componentConfig.visible[VisibilityConfigUtils.selectDisplayableTabKey(componentConfig.visible, viewCategory, audience, isPublicView)] || false;
	}

	public applyConditionalRules(componentId: string, componentData: ComponentData, clientOutput: ClientOutput, viewCategory: ViewCategory, audience: Audience, isPublicView?: VisibilityType): ComponentData {
		if (componentData) {
			let tabs: TabData[] = [];
			let tabsVisible: boolean;

			set(window, `AC_DATA["${componentId}"]`, componentData);

			componentData.tabs.forEach(tab => {
				const tabId = tab.id || (tab.config && tab.config.id);
				const tabConfig = this.componentVisibilityConfig[componentId].tabs[tabId] || {};

				// get tab config
				const defaultConfig = this.PDF_VIEW_CATEGORIES.includes(viewCategory) && tabConfig.defaultPdf ? tabConfig.defaultPdf : tabConfig.default;
				const config = VisibilityConfigUtils.selectDisplayableTab(tabConfig, viewCategory, audience, defaultConfig, isPublicView);
				const displayConfig: TabDisplayConfig = config['rule'] || new TabDisplayConfig();
				const isTabVisible = this.showTabInPublicView(displayConfig['showInPublicView'], isPublicView);

				if (displayConfig) {
					// TODO agrigore: change name of hideEmptyTab flag, it's confusing
					const isValidTabToShow = !displayConfig['hideEmptyTab'] || !TabUtil.isEmptyTab(tab);
					const isPartnerUserAndShouldHideTab = this.isPartnerUser && displayConfig['hideIfPartner'];
					const isNotPartnerUserAndShouldHideTab = !this.isPartnerUser && displayConfig['showInPartnerOnly'];
					// NOTE: for PDF, we hide the tab no matter it's because data points are not there or we transformed data to be empty
					if (clientOutput) {
						const newConfig = TabDisplayConfig.create(Object.assign({}, displayConfig, { clientOutput: clientOutput }));
						if (isValidTabToShow && !isPartnerUserAndShouldHideTab && !isNotPartnerUserAndShouldHideTab) {
							const transformedTab = this.applyTransforms(tab, componentData.portfolioData, newConfig);
							if (transformedTab && transformedTab.displayConfig.visible) {
								tabs.push(transformedTab);
							}
						}
					} else {
						/**
						 * NOTE: for web, if hideEmptyTab or hideIfPartner is true, we filter out tab when all data points are not there.
						 * If hideEmptyTab and hideIfPartner are false, we apply transformer to it and filter it out if transformed tab is null or visible is false.
						 * When all tabs are transformed to be empty, we show component(so that we can show DCR).
						 * If showInPublicView is true, then we show tab only if it's a public view in app otherwise if app is loaded in private view, 
						 * then we show all the tabs no matter value of showInPublicView
						 */
						if (isValidTabToShow && !isPartnerUserAndShouldHideTab && !isNotPartnerUserAndShouldHideTab && isTabVisible) {
							const transformedTab = this.applyTransforms(tab, componentData.portfolioData, displayConfig);
							if (transformedTab && transformedTab.displayConfig.visible) {
								tabs.push(transformedTab);
							} else {
								// tabsVisible is true when all tabs that transformed to be empty has hideEmptyTab flag
								tabsVisible = tabsVisible === undefined ? displayConfig['hideEmptyTab'] : tabsVisible && displayConfig['hideEmptyTab'];
							}
						}
					}
				} else {
					tabs.push(tab);
				}
			});

			// filter out the tabs with hideWithOtherTabs flag if there is no other tabs
			const hasDominantTab = tabs.some(tab => tab && tab.displayConfig && !tab.displayConfig.hideWithOtherTabs);
			tabs = hasDominantTab ? tabs : [];

			// Set error for the componentData as the current error (if exists) or the error of the dominant tab (if exists)
			const errorFromDominantTab = tabs.filter(tab => tab && tab.displayConfig && tab.displayConfig.isDominantTab && tab.error).map(tab => tab.error);
			const error = isEmpty(errorFromDominantTab) ? componentData.error : errorFromDominantTab[0];

			// At least one tab doesn't have error && the dominant tab doesn't have error (if it exists) && not loading && no error with component
			const success = TabUtil.hasTabWithNoError(tabs) && !componentData.isLoading && !error;

			const visible = componentData.tabs.length ? success && (tabs.length > 0 || tabsVisible) : componentData.visible;
			if (componentData.error) {
				const hideOnError = this.componentVisibilityConfig[componentId].hideOnError;
				componentData.error.hideOnError = hideOnError;
			}
			return new ComponentData(componentData.id, tabs, componentData.portfolioData, componentData.isLoading, error, success, visible);
		} else {
			console.error('Received falsy componentData', componentData);
			return new ComponentData(
				componentId,
				[],
				[],
				false,
				new ErrorData(componentId, false, false, [], 'Error getting component data', 'Data unavailable for chosen portfolios'),
				false
			);
		}
	}

	public transpose(tableData: TableData[]): TableData[] {
		return (
			(tableData &&
				tableData.map(data => {
					const result: CellData[][] = [];
					if (data && data.cells) {
						for (let i = 0; i < data.cells.length; i++) {
							for (let j = 0; j < data.cells[i].length; j++) {
								if (!result[j]) {
									result[j] = [];
								}
								result[j][i] = data.cells[i][j];
							}
						}
					}
					return new TableData(data.headersY, data.headersX, result, data.portfolioData, data.firstColumnTitle, true);
				})) ||
			null
		);
	}

	public getDisplayableComponentData(
		componentId: string,
		ids$: Observable<string[]>,
		contentService: ContentService,
		clientOutput?: ClientOutput,
		viewCategory$ = this.audienceService.getViewCategory(),
		isPublicView$ = this.audienceService.getVisibilityType()
	): Observable<ComponentData> {
		return combineLatest([viewCategory$, this.audienceService.getAudience(), isPublicView$]).pipe(
			switchMap(([viewCategory, audience, publicView]) => {
				console.log(`Preparing data for ${componentId} viewCategory: ${viewCategory} audience: ${JSON.stringify(audience)}`);
				let workingIds$ = ids$;
				if (this.checkComponentVisibilityForViewCategory(componentId, viewCategory, audience, publicView)) {
					const tabs = Object.values(this.componentVisibilityConfig[componentId].tabs);
					const tabConfigs = tabs.map(tab => {
						return VisibilityConfigUtils.selectDisplayableTab(tab, viewCategory, audience)['config'];
					}
					);
					const portfolioDataConfig = this.componentVisibilityConfig[componentId].portfolioDataExtractor;
					const idMapperObjects = get(this.componentVisibilityConfig, `['${componentId}'].idMapper`, {});
					const idMapper =
						get(idMapperObjects,
							`['${VisibilityConfigUtils.selectDisplayableTabKey(idMapperObjects, viewCategory, audience)}']`
						) ||
						get(this.componentVisibilityConfig, `['${componentId}'].idMapper.default`);
					if (idMapper) {
						console.warn(`DATA: Overriding default IDs source for ${componentId} from visibility config`);
						if (idMapper.includeDefaultIds) {
							workingIds$ = this._defaultIds$.pipe(mergeMap(ids => of([...ids, ...idMapper.ids])));
						} else {
							workingIds$ = of(idMapper.ids);
						}
					} else if (workingIds$) {
						console.warn(`DATA: Overriding default IDs source for ${componentId} from view-data-directive`);
					} else {
						workingIds$ = this._defaultIds$;
					}
					const componentData: Observable<ComponentData> = this.viewDataFactory.getComponentData(
						componentId,
						tabConfigs,
						portfolioDataConfig,
						workingIds$,
						contentService
					);
					return componentData.pipe(map(data => this.applyConditionalRules(componentId, data, clientOutput, viewCategory, audience, publicView)));
				} else {
					return of(new ComponentData(componentId, [], [], false, null, false, false));
				}
			})
		);
	}

	private applyTransforms(tab: TabData, portfolioData: CellRow[], displayConfig: TabDisplayConfig): TabData {
		let filteredTab = new TabData(tab.config, tab.tableData, tab.displayConfig || displayConfig, tab.error);
		(displayConfig?.conditions ?? []).forEach(condition => {
			if (filteredTab) {
				filteredTab = condition.transform(filteredTab, portfolioData);
			}
		});

		if (filteredTab) {
			return new TabData(
				tab.config,
				displayConfig.transpose && filteredTab.tableData ? this.transpose(filteredTab.tableData) : filteredTab.tableData,
				filteredTab.displayConfig || displayConfig,
				tab.error
			);
		}
		return null;
	}

	/**
	 * 
	 * @param showTabInPublicView 
	 * @param visibility 
	 * @returns tab visibility depending on application view and tab's showTabInLoggedIn value
	 * When it's a public view, this will return true only for tab which have showTabInLoggedIn as false
	 * otherwise in a private view, it'll always be returning true for all the tabs
	 */
	private showTabInPublicView(showTabInPublicView: boolean, visibility: VisibilityType): boolean {
		return visibility === VisibilityType.PUBLIC_VIEW ? showTabInPublicView : true;
	}
}
